import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as stylex from "@stylexjs/stylex";
import { getLabel } from "components/article-switcher";
import { motion } from "framer-motion";
import { ArrowLeft14 } from "icons/ArrowLeft";
import Link from "next/link";
import { useRouter } from "next/router";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";
import { useSwipeable } from "react-swipeable";
import { findArticleAlias, findArticleThemeAlias, subthemesIndex, useAlias } from "routes";
import useFitText from "use-fit-text";
import { foldChartType, useChartType } from "../../domain/hero-state";
import { useActiveTheme, useMedia, useRTL, useThemeNavigation } from "../../hooks";
import { breakpoints, colors, layoutAreas } from "../../materials/consts.stylex";
import { layout } from "../../materials/layout.stylex";
import { themeVars } from "../../materials/theme.stylex";
import { typography } from "../../materials/typography.stylex";
import { O, pipe } from "../../prelude";
import { HeroTooltip } from "./hero-tooltip";
import SubThemesNavigation from "./subthemes-nav";
import { TOCStatic } from "./TOC";
export const Hero = ({ children, title, caption, targetTitle, targetLabel, tooltipOverride })=>{
    const [chartType] = useChartType();
    const activeTheme = useActiveTheme();
    const isRTL = useRTL();
    const screenLUp = useMedia(breakpoints.l);
    const screenSDown = !useMedia(breakpoints.s);
    const alias = useAlias();
    const router = useRouter();
    const imageUrl = activeTheme.kind === "article" ? activeTheme.illustration : null;
    const tooltipPosition = foldChartType(chartType, {
        today: ()=>tooltipOverride === "finance" ? "todayFinance" : "today",
        timeseries: ()=>tooltipOverride === "finance" ? "timeseriesFinance" : "timeseries"
    });
    const aliasArticle = findArticleAlias(alias);
    const [prevTheme, nextTheme] = useThemeNavigation(pipe(findArticleAlias(alias), O.getOrElseW(()=>undefined)));
    const swipeLink = (alias)=>{
        if (alias) {
            router.push(`/articles/${alias}`);
        }
    };
    const swipeHandlers = useSwipeable({
        onSwipedRight: ()=>{
            isRTL ? swipeLink(nextTheme) : swipeLink(prevTheme);
        },
        onSwipedLeft: ()=>{
            isRTL ? swipeLink(prevTheme) : swipeLink(nextTheme);
        }
    });
    const hasSubthemes = pipe(aliasArticle, O.fold(()=>false, (a)=>subthemesIndex[findArticleThemeAlias(a)].length > 1));
    return <>
      <motion.section key={title?.toString() || "hero"} {...stylex.props(layout.gridCenteredMaxWidth, styles.root, hasSubthemes ? styles.rootPaddingWithSubthemes : styles.rootPaddingNoSubthemes)}>
        <div className="x1qd5a2c">
          <div className="x78zum5 x1q0g3np x6s0dn4 xou54vl">
            {screenLUp && prevTheme && <NavButton direction="previous" page={prevTheme}/>}
            <motion.div {...swipeHandlers} className="x10t73b6 xfa2u9r x12oqio5 x1ud1yml x12peec7 x78zum5 x12wiuel xdt5ytf xdjzykv" variants={cardVariants} custom={screenSDown} transition={{
        type: "spring",
        damping: 20,
        stiffness: 100
    }}>
              <div {...{
        0: {
            className: "x78zum5 xdt5ytf x1nhvcw1 x1n2onr6 x14atkfc x1n3m5j1 x193iq5w xx5ekxb x1hlb6i2 xk1wuvs xkyv6za x36t2ei x5hcv1j"
        },
        1: {
            className: "x78zum5 xdt5ytf x1nhvcw1 x1n2onr6 x14atkfc x1n3m5j1 x193iq5w xx5ekxb x1hlb6i2 xod5an3"
        }
    }[!!(tooltipOverride === "finance") << 0]}>
                {imageUrl && <img src={imageUrl} alt="" {...{
        0: {
            className: "x1sb9yw4 xxm4wck xz2sxpt xeoqwo7 xamitd3 x10l6tqk x1s6miji x1o310h0 xfst9wa x15l03u9 x1d81y6l xhq1jkn x1m1zoxh xuv6egx"
        },
        1: {
            className: "x1sb9yw4 xxm4wck xz2sxpt xeoqwo7 xamitd3 x10l6tqk x1s6miji x1o310h0 xfst9wa x15l03u9 x1d81y6l x1741ejh xmv20l xu1yaqc xq70poy x157zzsn"
        }
    }[!!isRTL << 0]}/>}
                <h1 {...stylex.props(typography.tera, styles.title, isRTL ? styles.titleMarginRtl : styles.titleMarginLtr)}>
                  <span {...stylex.props(typography.lead1, styles.titleLabel)}>
                    <FormattedMessage id="Theme" defaultMessage="Theme"/>
                  </span>
                  <FitText>{title}</FitText>
                </h1>
                <p {...stylex.props(typography.lead1, isRTL ? styles.leadTextRtl : styles.leadTextLtr)}>{caption}</p>
              </div>
              <div className="x1n2onr6 x14atkfc x1n3m5j1 x193iq5w x10tuow5 x2lah0s xdq7skj">
                {children}
                <HeroTooltip title={targetTitle} position={tooltipPosition}>
                  {targetLabel}
                </HeroTooltip>
              </div>
            </motion.div>

            {screenLUp && nextTheme && <NavButton direction="next" page={nextTheme}/>}
          </div>
          {screenLUp && pipe(aliasArticle, O.fold(()=>null, (a)=>subthemesIndex[findArticleThemeAlias(a)].length > 1 && <SubThemesNavigation/>))}
        </div>
      </motion.section>
      {pipe(aliasArticle, O.fold(()=>null, (a)=>subthemesIndex[findArticleThemeAlias(a)].length > 1 && <SubthemeTitle article={a}/>))}
      <div className="xfd2ba4">
        <TOCStatic/>
      </div>
    </>;
};
const NavButton = ({ direction, page })=>{
    const intl = useIntl();
    return <Link href={`/articles/${page}`} aria-label={direction === "next" ? intl.formatMessage(defineMessage({
        id: "Go to next theme",
        defaultMessage: "Go to next theme"
    })) : intl.formatMessage(defineMessage({
        id: "Go to previous theme",
        defaultMessage: "Go to previous theme"
    }))} className="x1k74hu9 xc342km xg7h5cd x12klvby x1ypdohk xhr9w20">
      <ArrowLeft14 {...{
        0: {
            className: "xvy4d1p xxk0z11"
        },
        1: {
            className: "xvy4d1p xxk0z11 x19jd1h0"
        }
    }[!!(direction === "next") << 0]}/>
    </Link>;
};
const SubthemeTitle = ({ article })=>{
    const screenLUp = useMedia(breakpoints.l);
    const subthemes = subthemesIndex[findArticleThemeAlias(article)];
    return <div id="subtheme-title">
      {screenLUp ? <h1 {...stylex.props(typography.title, styles.titleSubtheme)}>{getLabel(article)}</h1> : <DropdownMenu.Root>
          <DropdownMenu.Trigger {...stylex.props(typography.heading1Bold, styles.mobileSubthemeTrigger)}>
            {getLabel(article)}
            <ArrowLeft14 className="xrs2mdc x12klvby"/>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content onClick={(event)=>{
        event.stopPropagation();
    }} sideOffset={5} className="x78zum5 xdt5ytf x1n3tk9q x53tz7 xh8yej3 xmtjor9">
              {subthemes.map((subtheme)=><DropdownMenu.Item onClick={(event)=>{
            event.stopPropagation();
        }} asChild={true} {...{
            0: {
                className: "x1ff1495 x268jtm x1mqxbix"
            },
            1: {
                className: "x1ff1495 x268jtm x17ux3lg"
            }
        }[!!(subtheme === article) << 0]} key={subtheme}>
                  <Link href={`/articles/${subtheme}#subtheme-title`} onClick={(event)=>{
            event.stopPropagation();
        }}>
                    {getLabel(subtheme)}
                  </Link>
                </DropdownMenu.Item>)}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>}
    </div>;
};
const FitText = ({ children })=>{
    const { fontSize, ref } = useFitText();
    return <div ref={ref} style={{
        fontSize
    }} className="xnnlda6 x18m5iet xw0471e">
      {children}
    </div>;
};
const styles = {
    root: {
        kBCFzs: "x1lx81y2",
        kWkggS: "x19t24eb",
        kMwMTN: "x1rbwscb",
        kmkexE: "x1d1sdcu",
        kLKAdn: "xx4yyd5",
        k1K539: "xh7uebp xzo6th7 xom79l7",
        $$css: true
    },
    rootPaddingWithSubthemes: {
        kGO01o: "x18d9i69",
        $$css: true
    },
    rootPaddingNoSubthemes: {
        kGO01o: "x1j3kn9t",
        $$css: true
    },
    titleSubtheme: {
        kMwMTN: "x1rbwscb",
        $$css: true
    },
    mobileSubthemeTrigger: {
        kWkggS: "xjbqb8w",
        kI3sdo: "x1a2a7pz",
        kMzoRj: "xc342km",
        k1xSpc: "x78zum5",
        k9WMMc: "xdpxx8g",
        kOIVth: "x1665zp3",
        kMwMTN: "x1rbwscb",
        kGNEyG: "x6s0dn4",
        kmVPX3: "xe8ttls",
        kGzVvX: "xo65cfi",
        kLHA50: "xmcbsad",
        k8WPOF: "xuzxlxl",
        $$css: true
    },
    title: {
        keoZOQ: "x9otpla",
        k1K539: "x1e56ztr xhpmkff",
        kMwMTN: "x1rbwscb",
        $$css: true
    },
    titleMarginLtr: {
        km5ZXQ: "xcwxt3b x1ghmbdk xbwe6nh",
        koQZXg: "xj3b58b",
        $$css: true
    },
    titleMarginRtl: {
        km5ZXQ: "x1yf7rl7",
        koQZXg: "x1wxka03 x1ci6792 x1827vhn",
        $$css: true
    },
    titleLabel: {
        k1xSpc: "x1lliihq",
        $$css: true
    },
    leadTextLtr: {
        ks0D6T: "x9kgaae",
        km5ZXQ: "x1yf7rl7 x1xttyap x1r9u7ee xd6udtm",
        $$css: true
    },
    leadTextRtl: {
        ks0D6T: "x9kgaae",
        km5ZXQ: "x1yf7rl7",
        koQZXg: "x1gh5o4n x7q8cdi x7bz8ia",
        $$css: true
    }
};
const cardVariants = {
    show: (small)=>({
            opacity: 1,
            y: small ? 40 : 80
        }),
    hidden: (small)=>({
            opacity: 0,
            y: small ? 250 : -150
        })
};
